import { SystemOptionType } from 'data/types';
import { operatingCostBreakdownForOption } from 'utils/operating-cost-breakdown';
import { coolingRequiredOnOption } from 'utils/required-share-ratio';
import {
  includesKeptEquipmentEstimate,
  KEPT_EQUIPMENT_COST_NOTE
} from 'utils/ducted-system-metrics';
import { formatPrice } from 'utils/helpers';
import { ptacTerminalTwinMessage } from 'utils/ptac-operating-cost';
import {
  CostRow,
  OperatingCostAmount,
  OverviewCard,
  PerformanceOverviewCard
} from './overview-cards';
import ScoreRing from './score-ring';
import SystemCategoryScoreBars from './system-category-score-bars';

type SystemOverviewBandProps = Readonly<{
  system: SystemOptionType;
}>;

type SystemCardProps = Readonly<{
  system: SystemOptionType;
}>;

export function MatchScoreOverviewCard({ system }: SystemCardProps) {
  const totalScore = system.score?.total ?? 0;

  return (
    <OverviewCard title='Match Score'>
      {system.score ? (
        <div className='flex items-center gap-3'>
          <ScoreRing score={totalScore} size={48} thickness={6} fontSize={18} />
          <div className='min-w-0 grow'>
            <SystemCategoryScoreBars score={system.score} />
          </div>
        </div>
      ) : (
        <div className='text-2xl font-bold text-gray-900'>--</div>
      )}
    </OverviewCard>
  );
}

export function CostOverviewCard({ system }: SystemCardProps) {
  const keptEquipmentEstimate = includesKeptEquipmentEstimate(
    system.ductedOperatingCost
  );
  const annualCost = system.totalAnnualCost ?? null;
  const breakdown = operatingCostBreakdownForOption(system);
  const coolingNotRequired = !coolingRequiredOnOption(system);
  const twinMessage = ptacTerminalTwinMessage(system);
  const operatingCostSublabel = keptEquipmentEstimate ? (
    <>
      (Estimated)
      <div>{KEPT_EQUIPMENT_COST_NOTE}</div>
    </>
  ) : (
    <>
      (Estimated)
      {twinMessage && (
        <div
          data-testid='ptac-terminal-twin-note'
          className='font-semibold text-green-700'
        >
          {twinMessage}
        </div>
      )}
    </>
  );
  return (
    <OverviewCard title='Cost'>
      <div className='divide-y divide-gray-100'>
        <CostRow label='Equipment' sublabel='(Excludes installation)'>
          {formatPrice(system.price)}
        </CostRow>
        {breakdown === null ? (
          <CostRow label='Operating cost' sublabel={operatingCostSublabel}>
            {annualCost === null ? (
              '--'
            ) : (
              <OperatingCostAmount annualCost={annualCost} unit='month' />
            )}
          </CostRow>
        ) : (
          <>
            <CostRow
              label='Cooling operating cost'
              sublabel={
                coolingNotRequired ? '(No cooling required)' : '(Estimated)'
              }
            >
              <OperatingCostAmount
                annualCost={breakdown.coolingCost}
                unit='year'
              />
            </CostRow>
            <CostRow label='Heating operating cost' sublabel='(Estimated)'>
              <OperatingCostAmount
                annualCost={breakdown.heatingCost}
                unit='year'
              />
            </CostRow>
            {breakdown.auxHeat !== null && (
              <CostRow
                label={breakdown.auxHeat.label}
                sublabel={breakdown.auxHeat.sublabel}
              >
                <OperatingCostAmount
                  annualCost={breakdown.auxHeat.annualCost}
                  unit='year'
                />
              </CostRow>
            )}
            <CostRow
              label='Total operating cost'
              sublabel={operatingCostSublabel}
            >
              <OperatingCostAmount
                annualCost={breakdown.totalAnnualCost}
                unit='month'
              />
            </CostRow>
          </>
        )}
      </div>
    </OverviewCard>
  );
}

export default function SystemOverviewBand({
  system
}: SystemOverviewBandProps) {
  return (
    <div className='grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(230px,1fr))]'>
      <PerformanceOverviewCard system={system} />
      <MatchScoreOverviewCard system={system} />
      <CostOverviewCard system={system} />
    </div>
  );
}
